import { Box, CardActionArea, Skeleton, useTheme } from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import { ProductType } from "../../../all-types";
import { useEffect, useState } from "react";
import { NO_IMAGE_URL } from "../../../constants";

export interface ItemCardProps {
    handleOpen: (item: ProductType) => void;
    item: ProductType;
}

export default function ProductCard({ handleOpen, item }: ItemCardProps) {
    const theme = useTheme();
    const [isImageLoaded, setImageLoaded] = useState(false);
    const [imageSrc, setImageSrc] = useState(item.image);

    const isNotAvailableStyle = {
        filter: "grayscale(100%)",
        opacity: 0.5,
        backgroundColor: theme.palette.action.disabledBackground,
    };

    useEffect(() => {
        const img = new Image();
        img.src = item.image;

        const handleLoad = () => setImageLoaded(true);
        const handleError = () => {
            setImageSrc(NO_IMAGE_URL);
            setImageLoaded(true);
        };

        img.addEventListener("load", handleLoad);
        img.addEventListener("error", handleError);

        return () => {
            img.removeEventListener("load", handleLoad);
            img.removeEventListener("error", handleError);
        };
    }, [item.image]);

    return (
        <Card
            onClick={() => {
                if(item?.isInStock ) {
                    handleOpen(item)
                }
            }}
            sx={{
                ...(item?.isInStock ? {} : isNotAvailableStyle),
                display: "flex",
                flexDirection: "column",
                background: theme.palette.background.paper,
                backdropFilter: "blur(8px)",
                boxShadow: theme.shadows[2],
                maxWidth: "160px",
                width: "calc(50% - 16px)",
                height: "200px",
                borderRadius: "16px",
                border: `1px solid ${theme.palette.divider}`,
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: theme.shadows[4],
                },
                m: 1,
            }}
        >
            <CardActionArea sx={{ 
                display: "flex", 
                flexDirection: "column",
                flexGrow: 1,
                padding: "12px",
                color: theme.palette.text.primary,
            }}>
                {isImageLoaded ? (
                    <>
                        <Box sx={{
                            width: "100%",
                            height: "120px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            borderRadius: "12px",
                            mb: 1.5,
                            backgroundColor: theme.palette.background.default,
                        }}>
                            <CardMedia
                                component="img"
                                sx={{ 
                                    width: "auto", 
                                    maxWidth: "100%",
                                    height: "auto", 
                                    maxHeight: "100%",
                                    objectFit: "contain",
                                }}
                                image={imageSrc}
                                alt={item.name}
                                loading="lazy"
                            />
                        </Box>
                        <CardContent
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                flexGrow: 1,
                                width: "100%",
                                padding: 0,
                                "&:last-child": { paddingBottom: 0 },
                            }}
                        >
                            <Typography
                                gutterBottom
                                component="div"
                                sx={{ 
                                    fontWeight: 600, 
                                    fontSize: "0.95rem", 
                                    lineHeight: 1.3,
                                    color: theme.palette.text.primary,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {item.name}
                            </Typography>
                            {!item.isInStock && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: theme.palette.error.main,
                                        fontWeight: 500,
                                        mt: 0.5,
                                    }}
                                >
                                    Out of Stock
                                </Typography>
                            )}
                        </CardContent>
                    </>
                ) : (
                    <Skeleton
                        variant="rectangular"
                        sx={{
                            backgroundColor: theme.palette.action.hover,
                            borderRadius: "16px",
                            width: "100%",
                            height: "100%",
                        }}
                        animation="wave"
                    />
                )}
            </CardActionArea>
        </Card>
    );
}